/*!
 * Copyright 2023 Quisitive Technologies, LLC. All rights reserved.
 */
import React, { useState, useEffect } from 'react';

import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';

import Element from '../../../components/element';
import Button from '../../../components/Button';
import Flex from '../../../components/flex';
import Text from '../../../components/text';
import useStyles from './style';
import { colors } from '../../../config/theme';
import Demographics from './DemoGraphics';
import Cognition from './Cognition';
import Balance from './Balance';
import BodyComposition from './BodyComposition';
import VitalSigns from './VitalSigns';
import BloodDraw from './BloodDraw';
import PhysicalActivity from './PhysicalActivity';
import Sleep from './Sleep';
import Dexterity from './Dexterity';
import Strength from './Strength';
import MentalHealth from './MentalHealth';
import Nutrition from './Nutrition';
// import BodySymmetry from './BodySymmetry';
import Antropometrics from './Antropometrics';

const cognitionDisclaimer = `BrainCheck Assessment is a computerized cognitive assessment aid. This device is not intended to provide a
definitive diagnosis and should not be relied upon to diagnose any specific disease or condition. It is an aid in the diagnosis of
cognitive impairment, is not a standalone diagnostic device and should be confirmed by other testing. Nothing in the Assessment,
BrainCheck CQ™️ Report, or Braincheck CQ™️ Score should be considered or used as a substitute for medical advice or
diagnosis of any disease or condition by a qualified health provider. Please use BrainCheck Assessment with the intended use
population and in the intended use environment.`

const sleepDisclaimer = `The Pittsburgh Sleep Quality Index (PQSI) was developed by Buysse, D.J., Reynolds, C.F., Monk, T.H., Berman, S.R., and
Kupfer, D.J. of the University of Pittsburgh using National Institute of Mental Health Funding.

Copyright 1989 and 2010. University of Pittsburgh. All rights reserved.

Buysse DJ, Reynolds CF, Monk TH, Berman SR, Kupfer DJ: Psychiatry Research, 28:193-213, 1989.

This form may only be used for non-commercial education and research purposes. If you would like to use this instrument for commercial purposes or for commercially sponsored research, please contact the Innovation Institute at the University of Pittsburgh at 412-383-7670 for licensing information.`

const HealthReport = () => {
  const classes = useStyles();
  const location = useLocation();
  //   const dispatch = useDispatch();
  const navigate = useNavigate();

  const { state } = location;
  const { visit, assesmentValue, healthReportData, stationList } = state;
  const [assesmentTypeValue, setAssessmentValue] = useState(assesmentValue);

  const getAssesmentValue = e => {
    setAssessmentValue(Number(e.target.value));
  };

  const navigateToAssesments = () => {
    navigate('/landing/pastvisits');
  };
  const navigateToIndividualVisit = () => {
    navigate('/landing/pastvisits/individualVisit', {
      replace: true,
      state: {
        visit: visit
      }
    });
  };
  return (
    <Element>
      <Element>
        <Flex marginBottom="20px">
          <Element onClick={navigateToAssesments}>
            <Text family="GTAR" size="14px" color={colors.dustyGray}>
              Past Visits
            </Text>
          </Element>
          <Element onClick={navigateToIndividualVisit}>
            <Text family="GTAR" size="14px" color={colors.dustyGray}>
              &gt; Visit on {moment(visit.scheduledDate.substring(
                0,
                visit.scheduledDate.indexOf('T')
              )).format('MMMM Do, YYYY')}
            </Text>
          </Element>
          <Element>
            <Text family="GTAR" size="14px" color={colors.dustyGray}>
              &gt; Health Report
            </Text>
          </Element>
        </Flex>
      </Element>
      <Element
        className={classes.selectClass}
        paddingBottom="15px"
        borderBottom="1px solid #f6f6f6"
      >
        <label>Health Report Section</label>
        <select
          className={classes.selectOption}
          onChange={getAssesmentValue}
          value={assesmentTypeValue}
        >
          {stationList && stationList.map(station => (
            <option value={station.value}>{station.name}</option>
          ))}
        </select>
      </Element>
      {
        stationList[assesmentTypeValue].name === 'Demographics' && (
          <Demographics healthReportData={healthReportData} />
        )
      }
      {
        stationList[assesmentTypeValue].name === 'Physical Activity' && (
          <PhysicalActivity healthReportData={healthReportData} />
        )
      }
      {
        stationList[assesmentTypeValue].name === 'Sleep' && (
          <Sleep healthReportData={healthReportData} />
        )
      }
      {
        stationList[assesmentTypeValue].name === 'Mental Health' && (
          <MentalHealth healthReportData={healthReportData} />
        )
      }
      {
        stationList[assesmentTypeValue].name === 'Nutrition' && (
          <Nutrition healthReportData={healthReportData} />
        )
      }
      {
        stationList[assesmentTypeValue].name === 'Vital Signs' && (
          <VitalSigns healthReportData={healthReportData} />
        )
      }
      {
        stationList[assesmentTypeValue].name === 'Blood Draw' && (
          < BloodDraw healthReportData={healthReportData} />
        )
      }
      {
        stationList[assesmentTypeValue].name == 'Anthropometrics' && (
          <Antropometrics healthReportData={healthReportData} />
        )
      }
      {
        stationList[assesmentTypeValue].name === 'Body Composition' && (
          <BodyComposition healthReportData={healthReportData} />
        )
      }
      {
        stationList[assesmentTypeValue].name == 'Cognition' && (
          <Cognition healthReportData={healthReportData} />
        )
      }
      {
        stationList[assesmentTypeValue].name == 'Dexterity' && (
          <Dexterity healthReportData={healthReportData} />
        )
      }
      {
        stationList[assesmentTypeValue].name == 'Strength' && (
          <Strength healthReportData={healthReportData} />
        )
      }
      {
        stationList[assesmentTypeValue].name == 'Balance' && (
          <Balance healthReportData={healthReportData} />
        )
      }




      <Flex align="center" marginBottom="40px" marginTop="90px">
        <Button scheme="light" onClick={navigateToIndividualVisit}>
          Close
        </Button>
      </Flex>
      {
        (stationList[assesmentTypeValue].name === 'Cognition' || assesmentTypeValue === 2) && (
          <Element>
            {assesmentTypeValue == 2 && (<Element borderTop="2px solid #959595">
              <Text style={{ whiteSpace: "pre-line" }} family="GTAR" size="14px" color="#222222" marginTop="30px">
                <strong>Disclaimer: </strong>{sleepDisclaimer}
              </Text>
            </Element>)}
            {stationList[assesmentTypeValue].name === 'Cognition' && (
              <>
                <Element borderTop="2px solid #959595">
                  <Text style={{ whiteSpace: "pre-line" }} family="GTAR" size="14px" color="#222222" marginTop="30px">
                    <strong>Disclaimer: </strong>{cognitionDisclaimer}
                  </Text>
                </Element>
                <Element marginTop="30px">
                  <Text family="GTAR" size="14px" color="#222222">
                    <strong>Standard Score:</strong> Score scaled after comparing with
                    the normative population in the same age group and using the same
                    device. The mean score of the population is 100, and the standard
                    deviation is 15. Scores above (below) 100 indicate superior
                    (inferior) performance compared to the average performance of the
                    population.
                  </Text>
                </Element>
                <Element marginTop="30px">
                  <Text family="GTAR" size="14px" color="#222222">
                    <strong>Population Percentile:</strong> Relative rank of Standard
                    Score within the normative population in the same age group and
                    using the same device. For example, 70th Population Percentile
                    indicates 70% of test takers performed lower than the current test
                    taker, or, said another way, the current test taker performed
                    better than 70% of the population.
                  </Text>
                </Element>
                <Element marginTop="30px">
                  <Text family="GTAR" size="14px" color="#222222">
                    <strong>Timed out:</strong> The assessment was not completed
                    within the time limit, possibly due to the test taker being
                    distracted, not providing full effort, or too severely impaired to
                    complete testing. This is only applicable to the Stroop and Trail
                    Making assessments.
                  </Text>
                </Element>
                <Element marginTop="30px">
                  <Text family="GTAR" size="14px" color="#222222">
                    <strong>Malingering Test:</strong> Indicates intentional
                    production of lower performance based on performance on the
                    immediate and delayed recognition assessments
                  </Text>
                </Element>
                <Element marginBottom="100px" marginTop="30px">
                  <Text family="GTAR" size="14px" color="#222222">
                    <strong>Validity Test:</strong> Validity Test: Indicates whether
                    enough testing is completed to provide a score and whether the
                    score is within the boundaries of the normalized population. An
                    invalid test indicates the test taker was likely distracted or not
                    providing full effort.
                  </Text>
                </Element>
              </>
            )}
          </Element>
        )
      }
    </Element >
  );
};

export default HealthReport;
